import { For, Show, type JSX } from "solid-js";
import styles from "../../pages/Inventory.module.css";
import ItemStackView from "../ItemStackView";
import type { ItemStack } from "../../models/itemStack";

// The one item-grid tile rendering in the app: the Inventory page's storage panels, the Sellbot identity
// wizard's item picker, a shop's stock on ShopStock and a chest's contents on the storage page all
// render through this, so a visual or accessibility fix to one slot lands everywhere a slot appears
// instead of drifting between copies of the same markup.
export interface ItemSlotGridEntry {
    key: string;
    stack: ItemStack;
    label?: string;
    badge?: JSX.Element;
    extraClass?: string;
    draggable?: boolean;
    selected?: boolean;
    tooltipExtra?: JSX.Element | (() => JSX.Element);
}

export interface ItemSlotGridProps {
    items: ItemSlotGridEntry[];
    emptyMessage?: JSX.Element;
    compact?: boolean;
    // The event carries the modifier keys: ctrl toggles one tile, shift takes the range from the
    // last one clicked.
    onSelect?: (key: string, event: MouseEvent | KeyboardEvent) => void;
    onDragStart?: (e: DragEvent, key: string) => void;
    onDragEnd?: () => void;
}

export default function ItemSlotGrid(props: ItemSlotGridProps) {
    return (
        <Show when={props.items.length > 0} fallback={<Show when={props.emptyMessage}>
            <div class={styles.emptyState}>{props.emptyMessage}</div>
        </Show>}>
            <div class={`${styles.grid} ${props.compact ? styles.gridCompact : ""}`}>
                <For each={props.items}>{(entry) => (
                    <div
                        class={`${styles.slot} ${entry.selected ? styles.slotSelected : ""} ${entry.extraClass ?? ""}`}
                        role={props.onSelect ? "button" : undefined}
                        tabIndex={props.onSelect ? 0 : undefined}
                        aria-label={entry.label}
                        draggable={entry.draggable}
                        onDragStart={(e) => props.onDragStart?.(e, entry.key)}
                        onDragEnd={props.onDragEnd}
                        onClick={(e) => props.onSelect?.(entry.key, e)}
                        onKeyDown={(e) => {
                            if (props.onSelect && (e.key === "Enter" || e.key === " ")) props.onSelect(entry.key, e);
                        }}
                    >
                        <ItemStackView stack={entry.stack} size="100%" bare showCount={false} showTooltip
                            titleOverride={entry.label} tooltipExtra={entry.tooltipExtra} />
                        {entry.badge}
                    </div>
                )}</For>
            </div>
        </Show>
    );
}
